"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useAuthContext } from "@/platform/auth/AuthContext";
import { getUserReviews, freeReviewAllowanceUsed, deleteReview, setFreeContractUsed } from "@/platform/firebase/firestore/contract-reviews";
import type { ContractReviewFeedback, ContractReviewRecord } from "../types";
import { CONTRACT_TYPES, severityCounts, FREE_REVIEW_LIMIT } from "../types";
import { isFreePlan } from "@/modes/agentic-workflows/rfp-pack/planGate";
import { fetchOwnerFeedback } from "../api";
import ReviewFeedbackDialog from "./ReviewFeedbackDialog";
import ContractReviewTour, { ContractTourStartButton, useContractTour } from "./ContractReviewTour";
import "../contract-review.css";

type Filter = "all" | "needs" | "ready";

export default function ReviewsDashboard() {
  return (
    <ContractReviewTour variant="dashboard">
      <ReviewsDashboardInner />
    </ContractReviewTour>
  );
}

function ReviewsDashboardInner() {
  const router = useRouter();
  const tour = useContractTour();
  const { user, profile } = useAuthContext();
  const [reviews, setReviews] = useState<ContractReviewRecord[] | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [allowanceUsed, setAllowanceUsed] = useState(false);
  const [feedback, setFeedback] = useState<ContractReviewFeedback[]>([]);
  const [feedbackOpen, setFeedbackOpen] = useState<ContractReviewRecord | null>(null);

  const free = isFreePlan(profile?.subscription?.plan);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    getUserReviews(user.uid)
      .then((r) => {
        if (!cancelled) setReviews(r);
      })
      .catch(() => {
        if (!cancelled) setReviews([]);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    fetchOwnerFeedback()
      .then((list) => {
        if (!cancelled) setFeedback(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        /* non-fatal — counts just won't show */
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  const feedbackCountFor = (reviewId: string) =>
    feedback.filter((f) => f.review_id === reviewId).length;

  const list = reviews ?? [];
  const loading = reviews === null && Boolean(user);

  useEffect(() => {
    if (!user) return;
    if (!isFreePlan(profile?.subscription?.plan)) return;
    freeReviewAllowanceUsed(user.uid).then(setAllowanceUsed).catch(() => {});
  }, [user, profile]);

  const counts = (r: ContractReviewRecord) => severityCounts(r.clauses ?? []);

  const filtered = list.filter((r) => {
    const c = counts(r);
    if (filter === "needs" && c.red === 0) return false;
    if (filter === "ready" && c.red > 0) return false;
    if (typeFilter !== "all" && r.contract_type !== typeFilter) return false;
    return true;
  });

  const totalRed = list.reduce((a, r) => a + counts(r).red, 0);
  const ready = list.filter((r) => counts(r).red === 0).length;

  const freeAtLimit =
    !loading && isFreePlan(profile?.subscription?.plan) && (allowanceUsed || list.length >= FREE_REVIEW_LIMIT);

  const newControl = freeAtLimit ? (
    <button
      type="button"
      className="cr-btn-primary"
      data-tour="crd-new"
      onClick={() => router.push("/checkout?plan=pro_monthly")}
    >
      Upgrade to Pro
    </button>
  ) : (
    <button
      type="button"
      className="cr-btn-primary"
      data-tour="crd-new"
      onClick={() => router.push("/agentic-workflows/contract-reviews/new")}
    >
      + New review
    </button>
  );

  return (
    <div className="cr-root">
      <div className="cr-page-head">
        <h1>Supplier Contract Reviews</h1>
        <p className="cr-sub">
          Playbook-driven clause review, redlines, and negotiation strategy for vendor and supplier
          agreements — consistent every time. Sensei does not sign or send anything on your behalf.
        </p>
        {tour && !tour.active && (
          <div style={{ marginBottom: 18 }}>
            <ContractTourStartButton label="Take the tour" />
          </div>
        )}
      </div>

      <div className="cr-stats" data-tour="crd-stats">
        <div className="cr-stat-card">
          <div className="cr-stat-num">{list.length}</div>
          <div className="cr-stat-label">Total reviews</div>
        </div>
        <div className="cr-stat-card">
          <div className="cr-stat-num">{totalRed}</div>
          <div className="cr-stat-label">RED flags open</div>
        </div>
        <div className="cr-stat-card">
          <div className="cr-stat-num">{ready}</div>
          <div className="cr-stat-label">Ready for legal sign-off</div>
        </div>
      </div>

      <div className="cr-filter-row" data-tour="crd-filters">
        <div className="cr-pills">
          {([
            ["all", "All"],
            ["needs", "Needs review"],
            ["ready", "Ready"],
          ] as const).map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={`cr-pill ${filter === key ? "on" : ""}`}
              onClick={() => setFilter(key)}
            >
              {label}
            </button>
          ))}
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <select
            className="cr-select"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="all">All contract types</option>
            {CONTRACT_TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          {newControl}
        </div>
      </div>

      {freeAtLimit && (
        <div
          style={{
            marginBottom: 16,
            padding: "14px 18px",
            borderRadius: 14,
            border: "1px solid rgba(239,68,68,0.3)",
            background: "rgba(239,68,68,0.08)",
            fontSize: 13,
          }}
        >
          Free plan limit reached — you have used your {FREE_REVIEW_LIMIT} free contract review. Upgrade
          to Pro for unlimited reviews and exports.
        </div>
      )}

      {loading ? (
        <p style={{ color: "var(--cr-text-soft)" }}>Loading reviews…</p>
      ) : filtered.length === 0 ? (
        <div className="cr-empty">
          <p>No contract reviews here yet.</p>
          <button
            type="button"
            className="cr-btn-primary"
            onClick={() => router.push("/agentic-workflows/contract-reviews/new")}
          >
            Review a contract
          </button>
        </div>
      ) : (
        <div data-tour="crd-list">
          {filtered.map((r) => {
            const c = counts(r);
            return (
              <div
                key={r.id}
                className="cr-review-card"
                role="button"
                tabIndex={0}
                onClick={() => router.push(`/agentic-workflows/contract-reviews/${r.id}`)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") router.push(`/agentic-workflows/contract-reviews/${r.id}`);
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div className="cr-review-title">{r.document_name || "Untitled contract"}</div>
                  <div className="cr-review-meta">
                    {r.user_side === "supplier" ? "Supplier side" : "Buyer side"}
                    {" · "}
                    {r.contract_type || "Contract"}
                    {" · "}
                    {r.updated_at ? new Date(r.updated_at).toLocaleDateString() : "—"}
                    {" · "}
                    {r.review_basis === "client_playbook"
                      ? "Client playbook applied"
                      : "Standard positions used"}
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div className="cr-badge-row">
                    {c.red > 0 && <span className="cr-badge cr-badge-red">{c.red} RED</span>}
                    {c.yellow > 0 && <span className="cr-badge cr-badge-amber">{c.yellow} YELLOW</span>}
                    {c.red === 0 && c.yellow === 0 && <span className="cr-badge cr-badge-green">Ready</span>}
                  </div>
                  <button
                    type="button"
                    className="cr-fb-btn"
                    style={{ cursor: free ? "not-allowed" : "pointer", opacity: free ? 0.55 : 1 }}
                    disabled={free}
                    onClick={(e) => {
                      if (free) return;
                      e.stopPropagation();
                      setFeedbackOpen(r);
                    }}
                  >
                    Feedback
                    {feedbackCountFor(r.id) > 0 ? ` (${feedbackCountFor(r.id)})` : ""}
                  </button>
                  <button
                    type="button"
                    className="cr-fb-btn"
                    style={{ cursor: "pointer", opacity: 1 }}
                    onClick={async (e) => {
                      e.stopPropagation();
                      // Free plan: recording the used-allowance marker BEFORE the
                      // delete is the only remaining evidence that the allowance
                      // was consumed (the review doc itself is about to vanish).
                      if (user && free && !(await freeReviewAllowanceUsed(user.uid).catch(() => false))) {
                        await setFreeContractUsed(user.uid).catch(() => {});
                      }
                      await deleteReview(r.id).catch(() => {});
                      setReviews((prev) => (prev ?? []).filter((x) => x.id !== r.id));
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {feedbackOpen && (
        <ReviewFeedbackDialog
          reviewId={feedbackOpen.id}
          documentName={feedbackOpen.document_name}
          onClose={() => setFeedbackOpen(null)}
          onDeleted={(id) =>
            setFeedback((prev) => prev.filter((f) => f.id !== id))
          }
        />
      )}
    </div>
  );
}
